import {
	BillingStatus,
	FeatureFlags,
	Header as HeaderModule,
	Layout,
	Marketing,
	Modal,
	ShipStation,
	Toast,
	Utils,
} from 'common';
import AccountVerificationModal from 'common/src/code/accountVerification/AccountVerificationModal.component';
import { withLDConsumer } from 'launchdarkly-react-client-sdk';
import find from 'lodash/find';
import isArray from 'lodash/isArray';
import isFunction from 'lodash/isFunction';
import isNumber from 'lodash/isNumber';
import isObject from 'lodash/isObject';
import isString from 'lodash/isString';
import React, { Component } from 'react';
import Category from './Category';
import { FilterType } from './FilterType.enum';
import ProductsGuide from './guide/ProductsGuide';
import Product from './Product';
import ProductDetails from './ProductDetails.component';
import ProductsGrid from './ProductsGrid.component';
import ProductsLeftMenu from './ProductsLeftMenu.component';
import ProductsToolbar from './ProductsToolbar.component';
import ProductsCommunication from './utils/ProductsCommunication';

const {
	Logger,
	withLayoutAndModalContexts,
	Session,
	Analytics: AnalyticsUtils,
} = Utils;
const { ModalContainer } = Modal;
const { Header, Analytics } = HeaderModule;
const { ShipStationCommunication } = ShipStation;
const { Viewport, RegionType, CollapsibleSidePanel, CenterPanel, TopPanel } =
	Layout;
const { MarketingCampaign } = Marketing;
const { BillingStatusModal } = BillingStatus;
const { LaunchDarklyUtil } = FeatureFlags;
const { withToast } = Toast;

// Sorting column order for ProductsGrid
const SortDir = {
	Asc: 'asc',
	Desc: 'desc',
};
class MainViewCore extends Component {
	constructor(props) {
		super(props);

		const { accountStatus } = Session.getAccountInfo();

		let showEmailVerification = false;

		if (accountStatus === 'accountVerificationRequired') {
			showEmailVerification = true;
		}

		this.state = {
			activeMenuItemId: FilterType.All,
			categoryOptions: [],
			currentPage: 1,
			isAddingNewProduct: false, // `false` to allow users to add a new product
			isDetailsPanelHidden: true,
			leftMenuTitle: 'All Products',
			pageSize: 100,

			// WEBAPPS-27227
			// ag-grid's rowData should be `null` initially so:
			// 1) grid's loading overlay will show up automatically on page load, and
			// 2) grid's no-data overlay will not show up automatically.
			products: null,

			selectedProducts: [],
			sortDir: SortDir.Asc,
			sortBy: 'SKU',
			showPagination: true,
			tagOptions: [],
			term: '',
			totalCount: 0, // total number of products,
			showBillingStatusModal: Session.showBillingStatusModal(),
			showEmailVerification,
		};
		this.gridapi = null;
	}

	componentDidMount() {
		const { showBillingStatusModal, showEmailVerification } = this.state;
		const {
			modalContext: { showModal },
		} = this.props;

		if (showBillingStatusModal) {
			showModal({ component: BillingStatusModal });
		}

		const { ldClient } = this.props;
		if (ldClient) {
			Session.setFeatureFlags(ldClient.allFlags());
			LaunchDarklyUtil.logIntializeError(ldClient);
			const accountInfo = Session.getAccountInfo();
			AnalyticsUtils.initLogRocket(ldClient, accountInfo);
		}

		if (showEmailVerification) {
			showModal({
				component: AccountVerificationModal,
				data: {
					completionCallback: this.accountVerificationCallback,
					emailOnly: true,
				},
			});
		}
	}

	/**
	 * Call addProduct endpoint to add a new product
	 * @param {Product} product
	 */
	addNewProduct = async product => {
		const {
			modalContext: { showError },
		} = this.props;

		// remove temporary ProductID which is assigned at onClickAddProduct
		const response = await ProductsCommunication.addProduct({
			...product,
			ProductID: null,
		});

		if (!ProductsCommunication.isSuccessful(response)) {
			return showError({
				data: this.buildShowErrorData(response),
			});
		}

		const newProduct = Product(response.data);

		this.setState(
			({ products }) => ({
				isAddingNewProduct: false,
				products: [newProduct, ...products.slice(1)], // unshift the added product and remove the temporary one
				selectedProducts: [newProduct],
			}),
			this.fetchData
		);
	};

	// build query string to fetch product list
	buildQueryString = () => {
		const { activeMenuItemId, currentPage, sortBy, sortDir, term, pageSize } =
			this.state;
		const searchQuery = `sort=${sortBy}&dir=${sortDir}&page=${currentPage}&pageSize=${pageSize}&showInactive=false`;
		const [filterBy, itemId] = activeMenuItemId.split('_');

		if (filterBy === FilterType.Category) {
			return `${searchQuery}&category=${itemId}`;
		} else if (filterBy === FilterType.Search) {
			return `${searchQuery}&query=${term}`;
		} else if (filterBy === FilterType.Tag) {
			return `${searchQuery}&tag=${itemId}`;
		}

		// filterBy === FilterType.All
		return searchQuery;
	};

	// build error data for `showError` function
	buildShowErrorData = response => {
		const errorDescription =
			isObject(response) && isString(response.ErrorDescription)
				? response.ErrorDescription
				: '';
		const contactNumber = Util.getFraudNumber();

		return {
			title: 'Server Error',
			description: `
						<p>
							${errorDescription.replace(/(\\r\\n|\\n|\\r|"|"|\[|\])/g, '')}
						</p>
						<p>
							If this problem continues, please call Customer Support at
							${contactNumber} Monday – Friday, 8 am – 8 pm Central
							Time.
						</p>
					`,
			primaryButtonText: 'OK',
			handleType: 'custom',
		};
	};

	/**
	 * Call api endpoints to get the latest products, category and tags
	 */
	fetchData = async callback => {
		this.gridapi.showLoadingOverlay();

		const { selectedProducts } = this.state;
		const { products, total } = await this.getProducts();
		const categoryOptions = await this.getProductCategory();
		const tagOptions = await this.getTags();

		// check if products contain the ones were selected previously
		const newSelectedProducts = selectedProducts
			.map(({ ProductID }) => find(products, { ProductID }))
			.filter(product => product);

		// update UI
		this.setState(
			{
				categoryOptions,
				isAddingNewProduct: false,
				products,
				selectedProducts: newSelectedProducts,
				tagOptions,
				totalCount: total,
			},
			() => {
				this.refreshMainview();

				if (isFunction(callback)) {
					callback();
				}
			}
		);
	};

	fetchStoreStatus = async () => {
		const {
			modalContext: { showError },
		} = this.props;
		const response = await ShipStationCommunication.getStatus();

		if (ShipStationCommunication.isSuccessful(response)) {
			const { data = {} } = response;

			this.setState({
				storeStatus: isArray(data.storeStatus) ? data.storeStatus : [],
			});
		} else {
			showError({
				response,
			});
		}
	};

	/**
	 * Call getProducts endpoint
	 * @return {object}
	 */
	getProducts = async () => {
		const {
			modalContext: { showError },
		} = this.props;
		const searchQuery = this.buildQueryString();
		const response = await ProductsCommunication.getProducts(searchQuery);
		const isSuccessful = ProductsCommunication.isSuccessful(response);

		if (!isSuccessful) {
			showError({ response });
		}

		return {
			products:
				isSuccessful && isArray(response.data.products)
					? response.data.products.map(product => Product(product))
					: [],
			total:
				isSuccessful && isNumber(response.data.total) ? response.data.total : 0,
		};
	};

	/**
	 * Call getProductCategory endpoint
	 * @return {array}
	 */
	getProductCategory = async () => {
		const {
			modalContext: { showError },
		} = this.props;
		const response = await ProductsCommunication.getProductCategory();
		const isSuccessful = ProductsCommunication.isSuccessful(response);

		if (!isSuccessful) {
			showError(response);
		}

		return isObject(response.data) && isArray(response.data.categories)
			? response.data.categories.map(category => Category(category))
			: [];
	};

	/**
	 * Call getTags endpoint
	 * @return {array}
	 */
	getTags = async () => {
		const {
			modalContext: { showError },
		} = this.props;
		const response = await ProductsCommunication.getTags();
		const isSuccessful = ProductsCommunication.isSuccessful(response);

		if (!isSuccessful) {
			showError({ response });
		}

		return isObject(response.data) && isArray(response.data.tags)
			? response.data.tags
			: [];
	};

	/**
	 * Set east panels's visibility based on selectedProducts
	 */
	setEastPanelVisibile = () => {
		const { layoutContext } = this.props;
		const { selectedProducts, isDetailsPanelHidden } = this.state;

		const hideDetailsPanel = selectedProducts.length === 0;

		if (isDetailsPanelHidden !== hideDetailsPanel) {
			this.setState({ isDetailsPanelHidden: hideDetailsPanel }, () => {
				layoutContext.setHidden(RegionType.East, hideDetailsPanel);
			});
		}
	};

	/**
	 * Call getProductCategory endpoint to get the latest category options
	 * @param {function} successCallback
	 */
	refreshCategoryOptions = async successCallback => {
		const { activeMenuItemId } = this.state;
		const categoryOptions = await this.getProductCategory();
		const [filterBy, categoryId] = activeMenuItemId.split('_');

		// WEBAPPS-27280, reset to "All Products" tab view if the active tab has been deleted
		const nextActiveMenuItemId =
			filterBy === FilterType.Category &&
			!categoryOptions.some(({ CategoryID }) => CategoryID === +categoryId)
				? FilterType.All
				: activeMenuItemId;

		this.setState(
			{
				activeMenuItemId: nextActiveMenuItemId,
				categoryOptions,
			},
			() => {
				if (successCallback) {
					successCallback(categoryOptions);
				}

				this.refreshProducts();
			}
		);
	};

	/**
	 * Call getProducts endpoint to get the latest products
	 */
	refreshProducts = async () => {
		this.gridapi.showLoadingOverlay();

		const { selectedProducts } = this.state;
		const { products, total } = await this.getProducts();

		// check if products contain the ones were selected previously
		const newSelectedProducts = selectedProducts
			.map(({ ProductID }) => find(products, { ProductID }))
			.filter(product => product);

		this.setState(
			{
				isAddingNewProduct: false,
				products,
				selectedProducts: newSelectedProducts,
				totalCount: total,
			},
			this.refreshMainview
		);
	};

	/**
	 * Refresh product grid's UI and east panel's visibility
	 */
	refreshMainview = () => {
		// When column uses valueGetter or cellRenderer, and rowData is updated,
		// it could cause the row node data out of sync and the displayed text is not re-rendered.
		// Hence, we need to ask grid api to check the changes again and refresh the grid view.
		// Please refer to https://www.ag-grid.com/documentation/javascript/view-refresh/
		this.gridapi.refreshCells();

		// update overlay based on loading result
		this.updateGridOverlay();

		// update product grid's selections
		this.updateRowSelection();

		// update east panel's visibility
		this.setEastPanelVisibile();
	};

	/**
	 * Update product grid's overlay based on the total number of products
	 */
	updateGridOverlay = () => {
		const { products } = this.state;

		if (products.length === 0) {
			this.gridapi.showNoRowsOverlay();
		} else {
			this.gridapi.hideOverlay();
		}
	};

	/**
	 * Update selection in product grid based on selectedProducts
	 */
	updateRowSelection = () => {
		const { selectedProducts } = this.state;
		const selectedProductIds = selectedProducts.map(
			({ ProductID }) => ProductID
		);

		if (selectedProducts.length > 0 && this.gridapi) {
			this.gridapi.forEachNode(node => {
				const {
					data: { ProductID },
				} = node;
				const checked = selectedProductIds.includes(ProductID);

				node.setSelected(checked);
			});
		}
	};

	/**
	 * callback pass to ManageTagsModal component to update tag options
	 * @param {array} tagOptions
	 */
	onUpdateTagOptions = tagOptions => {
		const { activeMenuItemId } = this.state;
		const [filterBy, tagId] = activeMenuItemId.split('_');

		// WEBAPPS-27280, reset to "All Products" tab view if the active tab has been deleted
		const nextActiveMenuItemId =
			filterBy === FilterType.Tag &&
			!tagOptions.some(({ OrderTagID }) => OrderTagID === +tagId)
				? FilterType.All
				: activeMenuItemId;

		this.setState(
			{
				activeMenuItemId: nextActiveMenuItemId,
				tagOptions,
			},
			this.refreshProducts
		);
	};

	/**
	 * Adding/Updating by checking ProductID
	 * @param {object} product
	 */
	saveProduct = product => {
		// new product is given a temporary product id in onClickAddProduct
		if (product.ProductID === -1) {
			this.addNewProduct(product);
		} else {
			this.updateProduct(product);
		}
	};

	/**
	 * Call updateProduct endpoint to update existing product
	 * @param {object} updateProduct
	 */
	updateProduct = async updateProduct => {
		const {
			modalContext: { showError },
		} = this.props;
		const { products } = this.state;
		const response = await ProductsCommunication.updateProduct(updateProduct);

		if (!ProductsCommunication.isSuccessful(response)) {
			return showError({
				data: this.buildShowErrorData(response),
			});
		}

		const { data } = response;

		this.setState(
			{
				products: products.map(product => {
					if (product.ProductID === updateProduct.ProductID) {
						return Product(data.product);
					}

					return product;
				}),
			},
			this.fetchData
		);
	};

	onClickAddProduct = () => {
		const { isAddingNewProduct, products } = this.state;
		const newProduct = Product({ ProductID: -1 }); // use a temporary product id before this product gets added to the DB

		if (!isAddingNewProduct) {
			this.setState(
				{
					products: [newProduct, ...products],
					selectedProducts: [newProduct],
					isAddingNewProduct: true, // `true` to disable users from adding a new product
				},
				this.updateRowSelection
			);
		}
	};

	/**
	 * update the selected products' category
	 * @param {number} categoryId
	 */
	onChangeCategory = async categoryId => {
		const {
			modalContext: { showError },
		} = this.props;
		const { selectedProducts } = this.state;
		const multipleProductsData = selectedProducts
			.filter(
				product => product.ProductID !== -1 && product.CategoryID !== categoryId
			) // Exclude new product which hasn't been saved
			.map(product => ({ ...product, CategoryID: categoryId }));

		if (multipleProductsData.length === 0) {
			return;
		}

		const response =
			await ProductsCommunication.updateMultiple(multipleProductsData);

		if (ProductsCommunication.isSuccessful(response)) {
			this.fetchData();
		} else {
			showError({ response });
		}
	};

	/**
	 * callback to receive new page number from grid panel
	 * @param {number} currentPage
	 */
	onCurrentPageChange = currentPage => {
		this.setState(
			{
				currentPage,
				selectedProducts: [],
			},
			this.refreshProducts
		);
	};

	onDeleteProduct = async () => {
		const {
			modalContext: { showError },
		} = this.props;
		const { isAddingNewProduct, selectedProducts, products } = this.state;

		const multipleProductData = selectedProducts
			.filter(({ ProductID }) => ProductID !== -1)
			.map(product => ({ ...product, Active: false }));

		// remove the selected new product (unsaved) from products array
		if (
			isAddingNewProduct &&
			selectedProducts.find(({ ProductID }) => ProductID === -1)
		) {
			this.setState(
				{
					selectedProducts: [],
					products: products.slice(1),
					isAddingNewProduct: false,
				},
				() => {
					// hide the detail panel
					this.setEastPanelVisibile();
				}
			);
		}

		// make a call to delete products
		if (multipleProductData.length > 0) {
			const response =
				await ProductsCommunication.updateMultiple(multipleProductData);

			if (ProductsCommunication.isSuccessful(response)) {
				this.setState(
					{
						selectedProducts: [],
					},
					() => {
						// hide the detail panel
						this.setEastPanelVisibile();

						// refresh the data
						this.fetchData();
					}
				);
			} else {
				showError({ response });
			}
		}
	};

	onGridReady = ({ api }) => {
		this.gridapi = api;

		// fetch the store status data for product aliases
		this.fetchStoreStatus();

		// fetch data for products, category and tags
		this.fetchData(() => {
			const { modalContext, layoutContext } = this.props;
			const { ProductsWelcomeDisplayed } = Session.getPreferences();

			// WEBAPPS-28318, display tutorial modal for the new user (only in desktop platform)
			if (ProductsWelcomeDisplayed !== 'true' && layoutContext.isDesktop) {
				modalContext.showTutorial({
					id: 'Products',
					guide: ProductsGuide,
					onEnd: () => {
						Logger.logToServer({
							category: 'tutorial_info',
							action: 'learn_more',
							tutorialType: 'welcome',
						});

						// update the userPrefs so that tutorial modal won't show again
						Session.savePreferences([
							{
								Name: 'ProductsWelcomeDisplayed',
								Value: 'true',
							},
						]);
					},
				});
			}
		});
	};

	/**
	 * save products' selected tags
	 * @param {array} tagIds
	 */
	onChangeSelectedTags = async (tagIds = []) => {
		const {
			modalContext: { showError },
		} = this.props;
		const { selectedProducts } = this.state;
		const selectedProductsData = selectedProducts
			.filter(({ ProductID }) => ProductID !== -1) // Exclude new product which hasn't been saved
			.map(product => ({
				...product,
				Tags: tagIds,
				ProductTags: tagIds.map(id => ({
					ProductID: product.ProductID,
					OrderTagID: id,
				})),
			}));

		if (selectedProductsData.length === 0) {
			return;
		}

		const response =
			await ProductsCommunication.updateMultiple(selectedProductsData);

		if (ProductsCommunication.isSuccessful(response)) {
			this.refreshProducts();
		} else {
			showError({ response });
		}
	};

	collapseEastPanel = () => {
		const {
			layoutContext: { collapseSidePanel },
		} = this.props;
		collapseSidePanel(RegionType.East);
	};

	/**
	 * callback to receive product selection changes from grid panel
	 * @param {array} productIds
	 */
	onSelectionChanged = (products = []) => {
		this.setState(
			{
				selectedProducts: [...products],
			},
			this.setEastPanelVisibile
		);
	};

	/**
	 * callback to receive the sorting column and order from grid panel
	 * @param {string} sortBy
	 * @param {string} sortDir
	 */
	onSortChanged = (sortBy, sortDir) => {
		this.setState(
			{
				sortBy,
				sortDir,
			},
			this.refreshProducts
		);
	};

	/**
	 * Get the active menu from ProductsLeftMenu
	 * @param {object} data
	 */
	onMenuItemClick = data => {
		const { term } = this.state;
		const { activeMenuItemId, leftMenuTitle } = data;

		// WEBAPPS-27362, when users click search input (with empty value) and click somewhere else,
		// `onSearchInputBlur` event will be triggered in `SearchItem` component, causing to refresh products.
		// the following condition is added for `Search` grid view to only refresh products when `term` has changed
		if (activeMenuItemId === FilterType.Search && term === data.term) {
			return;
		}

		this.setState(
			{
				activeMenuItemId,
				currentPage: 1,
				leftMenuTitle,
				selectedProducts: [],
				term: data.term,
				totalCount: 0,
			},
			this.refreshProducts
		);
	};

	render() {
		const {
			activeMenuItemId,
			categoryOptions,
			currentPage,
			isAddingNewProduct,
			leftMenuTitle,
			products,
			pageSize,
			selectedProducts,
			showPagination,
			tagOptions,
			storeStatus,
			totalCount,
		} = this.state;

		return (
			<>
				{/* north region */}
				<TopPanel region={RegionType.North}>
					<Header />
					<Analytics />
				</TopPanel>
				{/* west region */}
				<CollapsibleSidePanel region={RegionType.West} title={leftMenuTitle}>
					<ProductsLeftMenu
						activeMenuItemId={activeMenuItemId}
						tagOptions={tagOptions}
						categoryOptions={categoryOptions}
						totalCount={totalCount}
						onMenuItemClick={this.onMenuItemClick}
					/>
				</CollapsibleSidePanel>
				{/* center */}
				<CenterPanel region={RegionType.Center}>
					<MarketingCampaign />
					<ProductsToolbar
						categoryOptions={categoryOptions}
						isAddingNewProduct={isAddingNewProduct}
						refreshCategoryOptionsCallback={this.refreshCategoryOptions}
						refreshProductsCallback={this.refreshProducts}
						selectedProducts={selectedProducts}
						tagOptions={tagOptions}
						onClickAddProductCallback={this.onClickAddProduct}
						onChangeCategoryCallback={this.onChangeCategory}
						onDeleteProductCallback={this.onDeleteProduct}
						onChangeSelectedTagsCallback={this.onChangeSelectedTags}
						onUpdateTagOptionsCallback={this.onUpdateTagOptions}
					/>
					<ProductsGrid
						categoryOptions={categoryOptions}
						products={products}
						currentPage={currentPage}
						activeMenuItemId={activeMenuItemId}
						pageSize={pageSize}
						tagOptions={tagOptions}
						totalCount={totalCount}
						showPagination={showPagination}
						onClickAddProductCallback={this.onClickAddProduct}
						onCurrentPageChange={this.onCurrentPageChange}
						onGridReady={this.onGridReady}
						onSelectionChangedCallback={this.onSelectionChanged}
						onSortChangedCallback={this.onSortChanged}
					/>
				</CenterPanel>
				{/* east region */}
				<CollapsibleSidePanel
					region={RegionType.East}
					title={
						selectedProducts.length === 1
							? 'Product Details'
							: 'Multiple Selections'
					}
					hideCollapsibleButton
				>
					<ProductDetails
						categoryOptions={categoryOptions}
						refreshCategoryOptionsCallback={this.refreshCategoryOptions}
						refreshProductsCallback={this.refreshProducts}
						saveProductCallback={this.saveProduct}
						selectedProducts={selectedProducts}
						tagOptions={tagOptions}
						onUpdateTagOptionsCallback={this.onUpdateTagOptions}
						onDeleteProductCallback={this.onDeleteProduct}
						collapseEastPanel={this.collapseEastPanel}
						storeStatus={storeStatus}
					/>
				</CollapsibleSidePanel>
			</>
		);
	}
}

// a higher-order component (HOC) that comes with layoutContext and modalContext objects
const MainViewCoreWithLayoutAndModal = withLayoutAndModalContexts(MainViewCore);
const MainViewWithLaunchDarkly = withLDConsumer()(
	withToast(MainViewCoreWithLayoutAndModal)
);

const MainView = () => {
	const layout = {
		[RegionType.North]: {
			height: 50,
			isHidden: false,
		},
		[RegionType.West]: {
			width: 230,
			minWidth: 50,
			maxWidth: 600,
			resizable: true,
			isCollapsed: false,
			isHidden: false,
		},
		[RegionType.East]: {
			width: 500,
			minWidth: 50,
			maxWidth: 600,
			resizable: true,
			isCollapsed: false,
			isHidden: true,
		},
	};

	return (
		<Viewport layout={layout}>
			<ModalContainer>
				<MainViewWithLaunchDarkly />
			</ModalContainer>
		</Viewport>
	);
};
export default MainView;
